import React, { useState, useRef, useEffect } from 'react';
import '../../styles/InputBox.css';
import fileDocxIcon from '../../assets/logos/fileDocx.svg';
import filePdfIcon from '../../assets/logos/filePdf.svg';
import fileXlsxIcon from '../../assets/logos/fileXlsx.svg';
import fileTxtIcon from '../../assets/logos/fileTxt.svg';
import fileGenericIcon from '../../assets/logos/file.svg';
import { Popover, List, ListItem, ListItemAvatar, ListItemText, Avatar, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import { ContentBlock } from '../../models/Message';
import { getSenderColor } from '../../utils/senderColor.ts';

interface MentionableUser {
  id: string;
  name: string;
  avatarUrl?: string;
  email?: string;
  role?: 'human' | 'assistant' | 'system';
}

interface InputBoxProps {
  onSend: (input: string, files: File[], contentBlocks: ContentBlock[]) => Promise<void> | void;
  onCancelFile?: (index?: number) => void;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  loading: boolean;
  error?: string;
  groupId: string;
  mentionableUsers?: MentionableUser[];
  initialDraft?: { html: string; files: File[] };
  onDraftChange?: (draft: { html: string; files: File[] }) => void;
}

const InputBox: React.FC<InputBoxProps> = ({
  onSend,
  onCancelFile,
  files,
  setFiles,
  loading,
  error,
  groupId,
  mentionableUsers = [],
  initialDraft,
  onDraftChange,
}) => {
  const [input, setInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editableRef = useRef<HTMLDivElement>(null);

  // Mention-related state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState<number | null>(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [mentions, setMentions] = useState<MentionableUser[]>([]);
  const selectedItemRef = useRef<HTMLLIElement>(null);
  const theme = useTheme();
  const mentionHighlight = alpha((theme.palette as any).chat?.userBubbleBg ?? '#1976D2', 0.06);

  const autoResize = () => {
    const editable = editableRef.current;
    if (editable) {
      editable.style.height = 'auto';
      const { scrollHeight } = editable;
      editable.style.overflowY = scrollHeight > 190 ? 'auto' : 'hidden';
      editable.style.height = `${Math.min(scrollHeight, 190)}px`;
    }
  };

  useEffect(() => {
    autoResize();
  }, [input]);

  // Used to skip the files effect on the initial mount, before the restore effect has run.
  // Without this, the files effect fires first (React runs effects in declaration order),
  // reads an empty innerHTML, and overwrites a valid saved draft with an empty one.
  const didMountRef = useRef(false);

  // Save draft when files change — skip initial mount
  useEffect(() => {
    if (!didMountRef.current) return;
    onDraftChange?.({ html: editableRef.current?.innerHTML ?? '', files: files ?? [] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  // Restore draft on mount
  useEffect(() => {
    didMountRef.current = true;
    if (initialDraft?.html && editableRef.current) {
      editableRef.current.innerHTML = initialDraft.html;
      setInput(editableRef.current.innerText || '');
      autoResize();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get plain text from contenteditable div
  const getPlainText = (element: HTMLElement): string => {
    let text = '';
    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || '';
      } else if (node.nodeName === 'SPAN' && (node as HTMLElement).classList.contains('mention')) {
        text += (node as HTMLElement).getAttribute('data-mention') || '';
      } else if (node.nodeName === 'BR') {
        text += '\n';
      } else if (node.nodeName === 'DIV') {
        // Only add newline before DIV content if we already have text
        if (text.length > 0) {
          text += '\n';
        }
        text += getPlainText(node as HTMLElement);
      } else if (node.childNodes.length > 0) {
        // Recursively process other element nodes
        text += getPlainText(node as HTMLElement);
      }
    });
    return text;
  };

  // Get cursor position in plain text
  const getCursorPosition = (): number => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount || !editableRef.current) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editableRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return getPlainText(preCaretRange.cloneContents() as any as HTMLElement).length;
  };

  // Set cursor position
  const setCursorPosition = (position: number) => {
    const editable = editableRef.current;
    if (!editable) return;

    let charCount = 0;
    let found = false;
    const range = document.createRange();
    const selection = window.getSelection();

    const traverse = (node: Node): boolean => {
      if (found) return true;

      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = (node.textContent || '').length;
        if (charCount + textLength >= position) {
          range.setStart(node, position - charCount);
          range.collapse(true);
          found = true;
          return true;
        }
        charCount += textLength;
      } else if (node.nodeName === 'SPAN' && (node as HTMLElement).classList.contains('mention')) {
        const mentionLength = ((node as HTMLElement).getAttribute('data-mention') || '').length;
        if (charCount + mentionLength >= position) {
          // Place cursor after the mention
          range.setStartAfter(node);
          range.collapse(true);
          found = true;
          return true;
        }
        charCount += mentionLength;
      } else {
        for (const child of Array.from(node.childNodes)) {
          if (traverse(child)) return true;
        }
      }
      return false;
    };

    traverse(editable);
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Filter mentionable users based on query
  const filteredMentions = React.useMemo(
    () =>
      mentionableUsers.filter(
        (user) =>
          user.name.toLowerCase().includes(mentionQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(mentionQuery.toLowerCase())
      ),
    [mentionableUsers, mentionQuery]
  );

  // Build contentBlocks array from contenteditable HTML
  const buildContentBlocks = (): ContentBlock[] => {
    if (!editableRef.current) return [];

    const contentBlocks: ContentBlock[] = [];
    let currentText = '';

    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        currentText += node.textContent || '';
      } else if (node.nodeName === 'SPAN' && (node as HTMLElement).classList.contains('mention')) {
        // Save any accumulated text before the mention
        if (currentText) {
          contentBlocks.push({ type: 'text', content: currentText });
          currentText = '';
        }
        // Add the mention block
        const userId = (node as HTMLElement).getAttribute('data-user-id');
        const displayText = (node as HTMLElement).getAttribute('data-mention');
        const role = (node as HTMLElement).getAttribute('data-role') as 'human' | 'assistant' | 'system' | null;
        if (userId && displayText) {
          contentBlocks.push({
            type: 'mention',
            userId: userId,
            displayText: displayText,
            ...(role && { role }),
          });
        }
      } else if (node.nodeName === 'BR') {
        currentText += '\n';
      } else if (node.nodeName === 'DIV') {
        // Handle div elements (new lines in contenteditable)
        if (currentText || contentBlocks.length > 0) {
          currentText += '\n';
        }
        node.childNodes.forEach(processNode);
      } else {
        // Process child nodes
        node.childNodes.forEach(processNode);
      }
    };

    editableRef.current.childNodes.forEach(processNode);

    // Add any remaining text
    if (currentText) {
      contentBlocks.push({ type: 'text', content: currentText });
    }

    return contentBlocks;
  };

  // Get coordinates at a specific text position
  const getPositionCoordinates = (position: number): { top: number; left: number } | null => {
    const editable = editableRef.current;
    if (!editable) return null;

    const selection = window.getSelection();
    if (!selection) return null;

    // Save current selection
    const currentRange = selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;

    // Create a range at the @ symbol position
    let charCount = 0;
    let targetNode: Node | null = null;
    let targetOffset = 0;

    const traverse = (node: Node): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = (node.textContent || '').length;
        if (charCount + textLength >= position) {
          targetNode = node;
          targetOffset = position - charCount;
          return true;
        }
        charCount += textLength;
      } else if (node.nodeName === 'SPAN' && (node as HTMLElement).classList.contains('mention')) {
        const mentionLength = ((node as HTMLElement).getAttribute('data-mention') || '').length;
        if (charCount + mentionLength >= position) {
          // Position is within or right after this mention
          // Since mentions are non-editable, place cursor after the mention
          const nextSibling = node.nextSibling;
          if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
            targetNode = nextSibling;
            targetOffset = 0;
          } else {
            // Use the mention node itself and we'll handle it with setStartAfter
            targetNode = node;
            targetOffset = -1; // Special flag to indicate we should use setStartAfter
          }
          return true;
        }
        charCount += mentionLength;
      } else {
        for (const child of Array.from(node.childNodes)) {
          if (traverse(child)) return true;
        }
      }
      return false;
    };

    traverse(editable);

    if (!targetNode) return null;

    // Create range at @ position
    const range = document.createRange();
    if (targetOffset === -1) {
      // Special case: position after a mention span
      range.setStartAfter(targetNode);
    } else {
      range.setStart(targetNode, targetOffset);
    }
    range.collapse(true);

    const rect = range.getBoundingClientRect();

    // Restore original selection
    if (currentRange) {
      selection.removeAllRanges();
      selection.addRange(currentRange);
    }

    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    };
  };

  // Detect @ mentions in input
  const detectMention = (text: string, cursorPos: number) => {
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol === -1) {
      setShowMentions(false);
      return;
    }

    // Check if @ is at the start or preceded by whitespace
    const charBeforeAt = lastAtSymbol > 0 ? textBeforeCursor[lastAtSymbol - 1] : null;
    const isValidMentionStart = lastAtSymbol === 0 || /\s/.test(charBeforeAt || '');

    if (!isValidMentionStart) {
      setShowMentions(false);
      return;
    }

    const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
    const hasSpaceAfterAt = textAfterAt.includes(' ') || textAfterAt.includes('\n');

    if (hasSpaceAfterAt) {
      setShowMentions(false);
      return;
    }

    setMentionQuery(textAfterAt);
    setMentionStartPos(lastAtSymbol);

    // Get @ symbol position for popover (position after the @)
    const coords = getPositionCoordinates(lastAtSymbol + 1);
    if (coords) {
      setAnchorPosition(coords);
      setShowMentions(true);
      setSelectedMentionIndex(0);
    }
  };

  // Handle paste to convert formatted content to plain text or handle image files
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    // Check if clipboard contains files (images)
    const items = e.clipboardData.items;
    const imageFiles: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image file
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    // If we found image files, add them to the files array
    if (imageFiles.length > 0) {
      const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      const validFiles: File[] = [];

      imageFiles.forEach((file) => {
        if (allowedImageTypes.includes(file.type)) {
          validFiles.push(file);
        } 
      });

      if (validFiles.length > 0) {
        setFiles((prev) => [...(prev || []), ...validFiles]);
      }
      return;
    }

    // If no images, handle as plain text
    let text = e.clipboardData.getData('text/plain');

    if (!text) return;

    document.execCommand('insertText', false, text);
  };

  // Handle input change for contenteditable
  const handleContentEditableInput = () => {
    if (!editableRef.current) return;

    const text = getPlainText(editableRef.current);
    const cursorPos = getCursorPosition();

    // If the text is only whitespace/newlines, clear the contenteditable
    if (text.trim() === '') {
      editableRef.current.innerHTML = '';
      setInput('');
      onDraftChange?.({ html: '', files: files ?? [] });
      return;
    }

    setInput(text);
    detectMention(text, cursorPos);
    onDraftChange?.({ html: editableRef.current.innerHTML, files: files ?? [] });
  };

  // Insert mention at cursor position
  const insertMention = React.useCallback((user: MentionableUser) => {
    if (mentionStartPos === null || !editableRef.current) return;

    const editable = editableRef.current;
    const selection = window.getSelection();
    if (!selection) return;

    // Find the text node and position where @ symbol starts
    let charCount = 0;
    let atNode: Node | null = null;
    let atOffset = 0;
    let endNode: Node | null = null;
    let endOffset = 0;

    const traverse = (node: Node): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = (node.textContent || '').length;

        // Check if @ symbol is in this node
        if (atNode === null && charCount + textLength > mentionStartPos) {
          atNode = node;
          atOffset = mentionStartPos - charCount;
        }

        // Check if end of mention query is in this node
        const mentionEndPos = mentionStartPos + mentionQuery.length + 1;
        if (endNode === null && charCount + textLength >= mentionEndPos) {
          endNode = node;
          endOffset = mentionEndPos - charCount;
          return true;
        }

        charCount += textLength;
      } else if (node.nodeName === 'SPAN' && (node as HTMLElement).classList.contains('mention')) {
        const mentionLength = ((node as HTMLElement).getAttribute('data-mention') || '').length;
        charCount += mentionLength;
      } else {
        for (const child of Array.from(node.childNodes)) {
          if (traverse(child)) return true;
        }
      }
      return false;
    };

    traverse(editable);

    if (!atNode || !endNode) return;

    // Create the mention element
    const mentionSpan = document.createElement('span');
    mentionSpan.className = 'mention';
    mentionSpan.contentEditable = 'false';
    mentionSpan.setAttribute('data-mention', `@${user.name}`);
    mentionSpan.setAttribute('data-user-id', user.id);

    // Base styles
    mentionSpan.style.display = 'inline';
    mentionSpan.style.fontWeight = '700';
    mentionSpan.style.cursor = 'default';
    mentionSpan.style.userSelect = 'none';

    if (user.role) {
      mentionSpan.setAttribute('data-role', user.role);
      if (user.role === 'assistant') {
        mentionSpan.classList.add('mention-assistant');
        mentionSpan.style.color = '#f57c00';
      }
    }
    if (user.role !== 'assistant') {
      mentionSpan.style.color = '#1565C0';
    }
    mentionSpan.textContent = `@${user.name}`;

    // Create a range to select the text to replace (@query)
    const range = document.createRange();
    range.setStart(atNode, atOffset);
    range.setEnd(endNode, endOffset);

    // Delete the selected text and insert the mention
    range.deleteContents();
    range.insertNode(mentionSpan);

    // Add a space after the mention
    const spaceNode = document.createTextNode('\u00A0');
    mentionSpan.parentNode?.insertBefore(spaceNode, mentionSpan.nextSibling);

    // Update the plain text input state
    const newText = getPlainText(editable);
    setInput(newText);
    setMentions(prev => [...prev, user]);
    setShowMentions(false);
    setMentionQuery('');
    setMentionStartPos(null);

    // Place cursor after the space
    setTimeout(() => {
      editable.focus();
      const newRange = document.createRange();
      newRange.setStartAfter(spaceNode);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }, 0);
  }, [mentionStartPos, mentionQuery]);

  // Auto-scroll to selected item in mention list
  useEffect(() => {
    if (selectedItemRef.current && showMentions) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedMentionIndex, showMentions]);

  // Handle keyboard navigation for mentions
  useEffect(() => {
    if (!showMentions || filteredMentions.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedMentionIndex((prev) =>
          prev < filteredMentions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedMentionIndex((prev) =>
          prev > 0 ? prev - 1 : filteredMentions.length - 1
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        if (filteredMentions[selectedMentionIndex]) {
          insertMention(filteredMentions[selectedMentionIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setShowMentions(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [showMentions, filteredMentions, selectedMentionIndex, insertMention]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((input.trim() || files?.length > 0) && !loading && input.length <= 5000) {
      const contentBlocks = buildContentBlocks();

      // Capture current state before clearing so we can restore on error
      const savedInput = input;
      const savedHtml = editableRef.current?.innerHTML ?? '';
      const savedFiles = [...(files ?? [])];
      const savedMentions = [...mentions];

      // Clear input immediately for responsive UX
      setInput('');
      setMentions([]);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (editableRef.current) editableRef.current.innerHTML = '';
      onDraftChange?.({ html: '', files: [] });

      try {
        await onSend(savedInput, savedFiles, contentBlocks);
      } catch (error) {
        // Restore input and files if send failed so the user can retry
        console.error('Failed to send message:', error);
        setInput(savedInput);
        setMentions(savedMentions);
        setFiles(savedFiles);
        if (editableRef.current) editableRef.current.innerHTML = savedHtml;
        onDraftChange?.({ html: savedHtml, files: savedFiles });
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const allowedTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    ];

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    selectedFiles.forEach((file) => {
      if (allowedTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      alert(`Invalid file types: ${invalidFiles.join(', ')}.`);
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // Only add to state, don't upload yet
    setFiles((prev) => [...(prev || []), ...validFiles]);

    e.target.value = '';
  };

  const handleCancelFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
    if (onCancelFile) onCancelFile(index);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (file: File): string => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf') return filePdfIcon;
    if (['doc', 'docx'].includes(ext)) return fileDocxIcon;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return fileXlsxIcon;
    if (ext === 'txt') return fileTxtIcon;
    return fileGenericIcon;
  };

  const getFileIconBg = (file: File): string => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf') return '#FEE2E2';
    if (['doc', 'docx'].includes(ext)) return '#FEF9C3';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '#D1FAE5';
    if (ext === 'txt') return '#F3F4F6';
    return '#EFF1F4';
  };

  const editableProps = {
    ref: editableRef,
    className: 'input-textarea input-editable',
    contentEditable: !loading as any,
    'data-placeholder': 'Ask me anything...',
    onInput: handleContentEditableInput,
    onPaste: handlePaste,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (showMentions && filteredMentions.length > 0) {
        if (e.key === 'Enter' || e.key === 'Tab') return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e as any);
      }
    },
    'aria-label': 'Input message',
    suppressContentEditableWarning: true,
  };

  const fileInput = (
    <input
      type="file"
      accept=".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
      style={{ display: 'none' }}
      ref={fileInputRef}
      data-testid="hidden-file-input"
      onChange={handleFileChange}
      aria-label="Upload file"
      multiple
    />
  );

  return (
    <>
      <div className="input-box-v2">
        <form className="input-form-v2" onSubmit={handleSubmit}>
          <div className="input-area-v2">
            <div {...editableProps} />
          </div>

          {files?.length > 0 && (
            <div className="files-in-box">
              {files.map((file, index) => (
                <div key={`${file.name}-${index}`} className="file-card-v2">
                  <div className="file-icon-v2" style={{ background: getFileIconBg(file) }}>
                    <img src={getFileIcon(file)} alt="" style={{ width: 20, height: 20 }} />
                  </div>
                  <div className="file-info-v2">
                    <span className="file-name-v2">{file.name}</span>
                    <span className="file-size-v2">{formatFileSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="file-remove-v2"
                    onClick={() => handleCancelFile(index)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <CloseIcon sx={{ fontSize: 16, color: '#9AA0A6' }} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="input-footer-v2">
            {fileInput}
            {input.length > 0 && (
              <div className="char-counter" aria-live="polite">{input.length}/5000</div>
            )}
            <button
              type="button"
              className="upload-button"
              data-testid="file-upload"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach file"
            >
              <AttachFileRoundedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            </button>
            <button
              type="submit"
              className={`send-button ${loading ? 'disabled' : ''}`}
              disabled={loading}
              aria-label="Send message"
            >
              {loading ? <div className="spinner" /> : <SendRoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
            </button>
          </div>
        </form>
      </div>

      {/* Mention Popover */}
      <Popover
        open={showMentions && filteredMentions.length > 0 && anchorPosition !== null}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || { top: 0, left: 0 }}
        onClose={() => setShowMentions(false)}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 280,
              minWidth: 300,
              maxWidth: 340,
              overflow: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              borderRadius: '12px',
              pointerEvents: 'auto',
            },
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
      >
        <List sx={{ padding: 0 }}>
          <Typography
            sx={{
              px: 2,
              pt: 1.5,
              pb: 0.5,
              display: 'block',
              fontSize: '12px',
              fontWeight: 500,
              color: '#9AA0A6',
              letterSpacing: '0.3px',
            }}
          >
            Suggestion
          </Typography>
          {filteredMentions.map((user, index) => (
            <ListItem
              key={user.id}
              ref={selectedMentionIndex === index ? selectedItemRef : null}
              onClick={() => insertMention(user)}
              sx={{
                cursor: 'pointer',
                backgroundColor: selectedMentionIndex === index
                  ? mentionHighlight
                  : 'transparent',
                '&:hover': {
                  backgroundColor: mentionHighlight,
                },
                transition: 'background-color 0.15s ease',
                padding: '10px 16px',
              }}
            >
              <ListItemAvatar sx={{ minWidth: 44 }}>
                {(() => {
                  const color = getSenderColor(user.id);
                  const isAgent = user.role === 'assistant';
                  return (
                    <Avatar
                      src={user.avatarUrl || undefined}
                      alt={user.name}
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: isAgent ? '#7C3AED' : color.avatarBg,
                        color: color.avatarFg,
                      }}
                    >
                      {isAgent ? <SmartToyIcon fontSize="small" /> : user.name.charAt(0).toUpperCase()}
                    </Avatar>
                  );
                })()}
              </ListItemAvatar>
              <ListItemText
                primary={user.name}
                secondary={user.email}
                slotProps={{
                  primary: {
                    sx: {
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#1A1F2E',
                    },
                  },
                  secondary: {
                    sx: {
                      fontSize: '12px',
                      color: '#7A8194',
                    },
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default InputBox;
